/**
 * The login-streak panel — the front end for a backend that has been complete and invisible.
 *
 * `GET /api/game/daily` and `POST /api/game/daily/claim` shipped with a seven-day catalogue, a
 * streak table and a service that credits soft currency and grants charms. Nothing called
 * either one. The reward existed, the reason to come back did not.
 *
 * It lives on Play rather than behind a tenth nav item, because a daily reward that has to be
 * navigated to is a daily reward nobody collects — the streak's whole job is to pay out on the
 * screen the player already lands on. Play's layout is edge-anchored by design (see
 * `play-route.ts`'s header), so this contributes one more bottom-left control above Community
 * and mirrors the right-hand action stack rather than putting a panel in the middle of the
 * menu the visual-language pass deliberately keeps empty.
 *
 * The seven tiles are a modal, not the button: seven tiles is the *explanation*, and it does
 * not belong on screen permanently in a layout whose whole argument is that the middle stays
 * clear. The button carries the one thing worth glancing at — whether there is something to
 * collect, shown as a gold dot and the day number in its label.
 *
 * **It does not auto-open, and that was a deliberate reversal.** The first version popped the
 * modal on arrival for any claimable day, on the reasoning that a reward you have to go and find
 * is a reward nobody collects. `shell:check` failed every one of the nine nav destinations at
 * both viewports: the modal's backdrop is `fixed inset-0 z-50`, so it intercepts every pointer
 * event in the shell, and until it was dismissed the entire nav was unreachable. That is not a
 * fixable detail of the modal — blocking the page is what a modal is *for* — so the choice was
 * between a modal nobody asked for and a nav that works. The dot costs one tap and the shell
 * stays usable.
 */
import { t } from '../i18n';
import { api } from '../../core/network/api';
import { button, icon, type IconName, openModal, plateContent } from './ui';
import { playSample, wireMenuSfx } from './menu-sfx';
import { rewardBurst } from './fx';
import { setNavBadge } from './nav-badges-store';
import { refreshPlayerProgress } from './player-progress-store';

interface DailyDay {
  day: number;
  rewardSoft: number;
  item: { id: string; name: string; description: string } | null;
  claimed: boolean;
  isToday: boolean;
}

interface DailyStatus {
  days: DailyDay[];
  currentDay: number;
  claimableToday: boolean;
}

interface ClaimResult {
  day: number;
  rewardSoft: number;
  item: { id: string; name: string } | null;
  softBalance: number;
}

export interface DailyRewardPanel {
  /** The wrapper to place; the caller owns where it goes. */
  element: HTMLElement;
  dispose(): void;
}

/**
 * The day plate's look — shared between the initial build and the in-place patch a successful
 * claim applies, so the two never drift into two slightly different ideas of what "claimed"
 * looks like.
 *
 * Every day is a painted steel plate (`kit-plate`) dealt in (`kit-deal`); today wears the teal
 * selection light (`kit-selected`) whether or not it has been collected yet — the calendar's one
 * focal point — and a collected day sits back at 75% under its stamp. Nothing loops except
 * today's icon bob, and only while there is something to take.
 */
function tileClasses(day: DailyDay): string {
  return [
    'kit-plate kit-deal [--kp-rim:12px] [--kp-pad:6px_4px_8px] relative flex flex-col items-center text-center min-w-0',
    day.isToday ? 'kit-selected' : '',
    day.claimed ? 'opacity-75' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/** The picture on a day's plate: a gift where a bonus item rides along, credits otherwise. */
function dayArt(day: DailyDay): IconName {
  return day.item ? 'gift' : 'credits';
}

/**
 * Builds the panel and starts its fetch. Returns synchronously so the caller can position it
 * immediately — same pattern the armoury and loadout routes use for their async chunks.
 */
export function mountDailyReward(): DailyRewardPanel {
  const element = document.createElement('div');
  element.className = 'flex flex-col items-start gap-2';

  // The launcher is a painted steel key (a `plate-key`), not a text slab: the 3D gift sits on
  // it, the label stacks over the day line, and a `kit-badge` rides the gift's corner while
  // there is something to take.
  const open = button(t('shell.dailyReward'), {
    variant: 'secondary',
    size: 'lg',
    className: 'pl-2.5 pr-4 text-[12px] justify-start gap-2.5',
    onClick: () => openPanel(),
  });
  wireMenuSfx(open);
  open.dataset.daily = 'open';
  // Hidden until the status arrives: a button that opens an empty modal is worse than no
  // button, and a failed fetch should leave the menu exactly as it was.
  open.classList.add('hidden');

  // Pinned to the gift's corner on every refresh — see below.
  const dot = document.createElement('span');
  dot.setAttribute('aria-hidden', 'true');
  dot.className = "kit-badge after:content-['!']";

  element.append(open);

  let status: DailyStatus | null = null;
  let closeModal: (() => void) | null = null;
  let disposed = false;

  /** Rebuilt on every open and after every claim: the modal's whole content is a function of
   * `status`, and seven tiles are cheaper to recreate than to diff. */
  function modalContent(onClaimed: () => void): Node[] {
    const current = status;
    if (!current) return [];

    // Seven upright day cards, not seven `itemTile` rows. `itemTile` is a horizontal
    // swatch-title-subtitle strip built for a full-width list; four of them per row inside a
    // 28rem dialog gave each one about 90px of usable width, which truncated every label to
    // "D…" and every reward to "4…". A day is a *stack* — number, crate, reward — and the
    // strip is the whole point of the panel, so it gets the room to be one.
    const row = document.createElement('div');
    row.className = 'grid grid-cols-4 sm:grid-cols-7 gap-2.5';

    current.days.forEach((day, index) => {
      const claimable = day.isToday && current.claimableToday;
      const tile = document.createElement('div');
      tile.dataset.day = String(day.day);
      tile.className = tileClasses(day);
      tile.style.setProperty('--i', String(index));

      // The day number is the plate's header, in game lettering — this is a calendar, and the
      // date is the primary key.
      const head = document.createElement('div');
      head.dataset.role = 'head';
      head.className = 'w-full flex items-center justify-center kit-label font-heading uppercase tracking-[0.12em] text-[11px]';
      head.textContent = t('shell.daily.day', { day: day.day });
      tile.append(head);

      // The reward picture, with the stamp over it once collected. Today's bobs while claimable.
      const artWrap = document.createElement('div');
      artWrap.className = 'relative my-1.5';
      const art = icon(dayArt(day), {
        size: 48,
        className: `w-11 h-11 sm:w-12 sm:h-12 [filter:drop-shadow(0_3px_3px_rgba(0,0,0,0.6))] ${claimable ? 'kit-float' : ''} ${day.claimed ? 'opacity-50' : ''}`,
      });
      artWrap.append(art);
      if (day.claimed) artWrap.append(icon('done', { size: 32, className: 'absolute inset-0 m-auto kit-pop' }));
      tile.append(artWrap);

      const reward = document.createElement('span');
      reward.className = 'kit-chip [--kh:24px] font-heading text-[12px] tabular-nums kit-label';
      reward.append(icon('credits', { size: 18 }));
      const amount = document.createElement('span');
      amount.textContent = String(day.rewardSoft);
      reward.append(amount);
      tile.append(reward);

      // The bonus item, on the days that carry one. Named, not implied by a picture.
      const extra = document.createElement('span');
      extra.className = 'block w-full px-1 pt-1 text-[10px] leading-tight text-text-dim truncate';
      extra.textContent = day.item ? day.item.name : '—';
      if (!day.item) extra.classList.add('opacity-40');
      tile.append(extra);

      row.append(tile);
    });

    const strip = document.createElement('div');
    strip.className = 'mb-2';
    strip.append(row);

    const today = current.days.find((day) => day.day === current.currentDay) ?? null;
    const note = document.createElement('p');
    note.className = 'text-[13px] text-text';
    // Says what today actually pays, not just that today exists. "Day 3 is ready" tells a
    // player nothing they cannot see from the strip; the amount is the reason to press.
    note.textContent = current.claimableToday
      ? today?.item
        ? t('shell.daily.todayWithItem', {
            day: current.currentDay,
            credits: today.rewardSoft,
            item: today.item.name,
          })
        : t('shell.daily.today', { day: current.currentDay, credits: today?.rewardSoft ?? 0 })
      : t('shell.daily.collectedToday', { day: current.currentDay });

    const status_ = document.createElement('p');
    status_.className = 'text-[12px] text-text-dim min-h-[1.2em] mt-0.5';
    status_.setAttribute('role', 'status');

    const claim = button(current.claimableToday ? t('common.claim') : t('shell.collected'), {
      variant: 'primary',
      size: 'lg',
      className: 'px-10',
    });
    wireMenuSfx(claim);
    claim.dataset.daily = 'claim';
    claim.disabled = !current.claimableToday;
    // The plate's own `:disabled` look greys it; this keeps a touch from landing on it at all.
    claim.classList.toggle('pointer-events-none', !current.claimableToday);

    /** Into the plate's own label node: the face paints over any other child. */
    function setClaimLabel(text: string): void {
      plateContent(claim).textContent = text;
    }

    claim.addEventListener('click', () => {
      void (async () => {
        claim.disabled = true;
        // Busy is a state, not just a disabled attribute a touch device cannot feel — the label
        // itself says something is in flight.
        setClaimLabel(t('common.claiming'));
        status_.textContent = t('common.claiming');
        try {
          const result = await api.post<ClaimResult>('/game/daily/claim', {});
          if (disposed) return;
          status_.textContent = result.item
            ? t('shell.daily.claimedWithItem', {
                credits: result.rewardSoft,
                item: result.item.name,
              })
            : t('shell.daily.claimedCredits', { credits: result.rewardSoft });

          // Patch the claimed tile in place rather than rebuilding the strip: the player is
          // looking at seven cards together, and the one thing that should visibly change is
          // the one they just pressed.
          const claimedTile = row.querySelector<HTMLElement>(`[data-day="${result.day}"]`);
          // `today` was resolved above from `current.days` — the claim button only ever claims
          // the one claimable day, so this is the source-of-truth record for the card being
          // patched, without a second lookup into `current.days`.
          if (claimedTile && today) {
            const claimedDay: DailyDay = { ...today, claimed: true };
            playSample('collect');
        rewardBurst(claimedTile, { label: `+${result.rewardSoft}`, tone: result.item ? 'violet' : 'gold' });
            claimedTile.className = tileClasses(claimedDay);
            // Stamp it: the picture sits back and the `done` mark lands over it.
            const artWrap = claimedTile.querySelector<HTMLElement>('div.relative');
            const art = artWrap?.querySelector('img');
            art?.classList.remove('kit-float');
            art?.classList.add('opacity-50');
            artWrap?.append(icon('done', { size: 32, className: 'absolute inset-0 m-auto kit-pop' }));
          }

          claim.disabled = true;
          claim.classList.add('pointer-events-none');
          setClaimLabel(t('shell.collected'));

          // The claim moved the soft balance, so the header's pills are now stale — this is
          // exactly the invalidation `player-progress-store.ts` said was missing while nothing
          // but the armoury could spend.
          void refreshPlayerProgress();
          // Quietly resyncs `status` (and the nav dot) for the NEXT open — not `onClaimed()`,
          // which reopens the modal and rebuilds all seven tiles to change the one above.
          void refresh();
        } catch (error) {
          if (disposed) return;
          // A claim refused after the screen believed it was claimable means the day rolled or
          // another tab got there first — the local patch above never ran, so a full reread and
          // rebuild is the only way back to a screen that matches the server.
          claim.disabled = false;
          setClaimLabel(t('common.claim'));
          status_.textContent = t('shell.daily.claimFailed', { message: (error as Error).message });
          await refresh();
          if (!disposed) onClaimed();
        }
      })();
    });

    // A footer bar rather than a button loose under a paragraph: the strip is the content and
    // this is the one action, so it gets its own band across the bottom of the dialog.
    const actions = document.createElement('div');
    actions.className = 'flex flex-wrap items-center justify-between gap-3 mt-2';
    const copy = document.createElement('div');
    copy.className = 'kit-well min-w-0 flex-1 basis-48 px-4 py-2.5';
    copy.append(note, status_);
    actions.append(copy, claim);

    return [strip, actions];
  }

  function openPanel(): void {
    if (!status || closeModal) return;
    // Re-open in place after a claim so the tiles repaint: `openModal` builds its content once,
    // so the modal is replaced rather than mutated.
    const reopen = () => {
      closeModal?.();
      closeModal = null;
      openPanel();
    };
    closeModal = openModal({
      title: t('shell.daily.modalTitle', { day: status.currentDay }),
      width: 'xl',
      content: modalContent(reopen),
      onClose: () => {
        closeModal = null;
      },
    });
  }

  /**
   * Reads the status, repaints the button, and hands the value back.
   *
   * Returning it rather than only assigning `status` is what lets the auto-open below read a
   * value the compiler can narrow: assignments made inside this function are invisible to
   * control-flow analysis at the call site, where `status` is still its `null` initialiser.
   */
  async function refresh(): Promise<DailyStatus | null> {
    try {
      const fresh = await api.get<DailyStatus>('/game/daily');
      if (disposed) return null;
      status = fresh;
      // Only ever from the server's own answer — see the store's note on why a dot must never
      // be optimistic.
      setNavBadge('daily', fresh.claimableToday);
      open.classList.remove('hidden');
      // Into the plate's own label node, never into the button. `plate-key`'s face (`::after`)
      // paints above any child that is not `.plate-key-label`, so a `replaceChildren()` here
      // rendered a flawless empty plate — which is exactly what it looked like.
      const face = plateContent(open);
      face.replaceChildren();
      // The gift on the left: a closed crate normally, a bobbing gift with a `kit-badge` when
      // there is something to take, which is the whole message read before any word is.
      const crateWrap = document.createElement('span');
      crateWrap.className = 'relative shrink-0';
      crateWrap.append(icon(fresh.claimableToday ? 'gift' : 'crate', {
        size: 40,
        className: fresh.claimableToday ? 'kit-float' : 'opacity-80',
      }));
      if (fresh.claimableToday) crateWrap.append(dot);
      const label = document.createElement('span');
      label.className = 'flex flex-col items-start leading-tight text-left';
      const title = document.createElement('span');
      title.className = 'font-heading font-bold uppercase tracking-[0.12em] text-[12px]';
      title.textContent = t('shell.dailyReward');
      label.append(title);
      const sub = document.createElement('span');
      sub.className =
        'font-heading uppercase tracking-[0.1em] text-[10px] ' +
        (fresh.claimableToday ? 'text-gold' : 'text-text-dim');
      sub.textContent = fresh.claimableToday
        ? t('shell.daily.ready', { day: fresh.currentDay })
        : t('shell.daily.day', { day: fresh.currentDay });
      label.append(sub);
      face.append(crateWrap, label);
      open.setAttribute(
        'aria-label',
        fresh.claimableToday
          ? t('shell.daily.readyAria', { day: fresh.currentDay })
          : t('shell.dailyReward'),
      );
      return fresh;
    } catch {
      // Left hidden. A streak nobody can read is not worth an error message on the menu — the
      // player's next action is PLAY either way, and the reward is still there tomorrow.
    }
    return null;
  }

  void refresh();

  return {
    element,
    dispose() {
      disposed = true;
      closeModal?.();
      closeModal = null;
      element.remove();
    },
  };
}
